import React, { useRef, useState } from 'react';

import type { ListDefinition } from '@aline/common';
import { listDefinitionPresets } from '@aline/common';
import { Paper, Popover } from '@mui/material';
import { equals, keys } from 'ramda';
import { AlineTooltip, SelectButton } from 'src/components';
import { expandedListIcons } from 'src/components/icons/expandedListIcons';
import { SmallDownArrowIcon } from 'src/components/icons/SmallDownArrowIcon';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  arrowIcon: {
    height: 12,
    marginLeft: theme.spacing(0.25),
    width: 12,
  },
  paper: {
    padding: theme.spacing(1),
    zIndex: theme.zIndex.tooltip,
  },
  selectButton: {
    height: 32,
    width: 46,
  },
  toggleButton: {
    height: 100,
    padding: 0,
    width: 80,
  },
  toggleButtonContainer: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

interface ListButtonProps {
  icon: JSX.Element;
  listDefinitionIndices: number[];
  tooltipTitle: string;
}

export function ListButton(props: ListButtonProps): JSX.Element {
  const { icon, listDefinitionIndices, tooltipTitle } = props;
  const { classes } = useStyles();
  const editor = useEditor();
  const editorLoading = useEditorLoading();
  const currentUserId = useCurrentUserId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const activeListDefinition = useEditorTabStateSelector(
    state => state.derived.listProperties.listDefinition,
    equals
  );

  function getAreListStylesEqual(listDefA: ListDefinition, listDefB: ListDefinition): boolean {
    return keys(listDefA).every(listLevel => {
      const { style: styleAAtLevel } = listDefA[listLevel] ?? {};
      const { style: styleBAtLevel } = listDefB[listLevel] ?? {};
      return equals(styleAAtLevel, styleBAtLevel);
    });
  }

  return (
    <>
      <AlineTooltip
        arrow
        open={tooltipOpen && !popoverOpen}
        placement="bottom"
        title={tooltipTitle}
        onClose={() => setTooltipOpen(false)}
        onOpen={() => setTooltipOpen(true)}
      >
        <div>
          <SelectButton
            ref={buttonRef}
            className={classes.selectButton}
            disabled={editorLoading}
            selected={popoverOpen}
            onClick={(): void => {
              setPopoverOpen(!popoverOpen);
            }}
          >
            {icon}
            <SmallDownArrowIcon className={classes.arrowIcon} />
          </SelectButton>
        </div>
      </AlineTooltip>
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={popoverOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={(): void => setPopoverOpen(false)}
      >
        <Paper className={classes.paper}>
          <div className={classes.toggleButtonContainer}>
            {listDefinitionIndices.map(number => {
              return (
                <SelectButton
                  key={number}
                  className={classes.toggleButton}
                  selected={
                    !!activeListDefinition &&
                    getAreListStylesEqual(listDefinitionPresets[number], activeListDefinition)
                  }
                  variant="outlined"
                  onClick={() => {
                    if (!currentUserId || !editingMode) return;

                    AuthorizedTransforms.toggleListItems(editor, {
                      authorId: currentUserId,
                      editingMode,
                      listDefinition: listDefinitionPresets[number],
                    });

                    setPopoverOpen(false);
                    ClientTransforms.focus(editor);
                  }}
                >
                  {React.createElement(expandedListIcons[number])}
                </SelectButton>
              );
            })}
          </div>
        </Paper>
      </Popover>
    </>
  );
}
